import { useTranslation } from 'react-i18next'
import { TAB_TO_SCREEN_MAP } from '../../../../../../data/components'
import { useAppDispatch } from '../../../../../../redux/hooks'
import { setScreen } from '../../../../../../redux/reducers/messenger/screenSlice'
import { Image } from '../../../../../../shared/image'
import { Message } from '../../../../../../types/api'
import AnnouncementVideoPlayer from './AnnouncementVideoPlayer'

// ─── helpers ────────────────────────────────────────────────────────────────

const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'm4v', 'ogv', 'ogg'])

/**
 * Returns true when the message's file is a video.
 * Checks file_type (MIME) first, then falls back to URL extension so that
 * both newly uploaded files (which have file_type set) and legacy records
 * (which may only have a file_url) are handled correctly.
 */
const isVideoMedia = (message: Message): boolean => {
  const mime = message.file_type ?? message.metadata?.mime_type
  if (mime) return mime.startsWith('video/')
  const url = String(message.file_url ?? '')
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase() ?? ''
  return VIDEO_EXTENSIONS.has(ext)
}

// ─── component ───────────────────────────────────────────────────────────────

const AnnouncementMessage = ({ message }: { message: Message }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const handleKey = () => {
    if (message?.metadata?.announcement_type === 'get_started') {
      const redirectTab = message.metadata.redirect_url
      if (!redirectTab) return
      const screen = TAB_TO_SCREEN_MAP[redirectTab]
      if (screen) dispatch(setScreen(screen))
    } else if (message?.metadata?.announcement_type === 'learn_more') {
      const url = message?.metadata?.action_link
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  const hasMedia = Boolean(message.file_url)
  const showVideo = hasMedia && isVideoMedia(message)

  return (
    <div className="announce-preview-card">
      <div className="announce-preview-card-inner">
        {/* ── media ─────────────────────────────────────────────────────── */}
        {hasMedia && (
          <div className="announce-preview-content" style={{ overflow: 'hidden' }}>
            {showVideo ? (
              <AnnouncementVideoPlayer src={String(message.file_url)} className="announce-preview-image" />
            ) : (
              <Image src={message.file_url} alt="Announcement media" className="announce-preview-image" />
            )}
          </div>
        )}

        {/* ── text content ──────────────────────────────────────────────── */}
        {message?.metadata?.title && <div className="announce-preview-card-title">{message.metadata.title}</div>}
        {message.content && <div className="announce-preview-card-desc">{message.content}</div>}

        {/* ── CTA button ────────────────────────────────────────────────── */}
        {message?.metadata?.announcement_type && message.metadata.announcement_type !== 'none' && (
          <>
            <hr />
            <div className="d-flex justify-content-center" onClick={handleKey}>
              <button className="announce-preview-card-btn">{t(message.metadata.announcement_type)}</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default AnnouncementMessage
